import { useRef, type KeyboardEvent, type ReactNode } from 'react';

/**
 * Generic, accessible tabbed container implementing the WAI-ARIA Tabs pattern:
 * tablist/tab/tabpanel roles, aria-selected, roving tabindex, arrow/Home/End key
 * navigation, and automatic activation (selection follows focus).
 *
 * The component is purely presentational — it renders exactly the tabs it is
 * given (callers pre-filter to the AVAILABLE tabs) and renders a panel element
 * for EVERY tab so that all provided panels stay mounted; only the selected one
 * is visible. It knows nothing about any specific domain.
 *
 * Visual tokens mirror Settings.tsx's TabButton so the control looks native to
 * the app.
 *
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 9.1, 9.2, 9.3, 9.4, 9.5, 9.6.
 */

export interface TabDescriptor {
  /** Stable identity for the tab, e.g. 'market-data'. Drives element ids. */
  id: string;
  /** Visible, human-readable tab label, e.g. 'Market Data'. */
  label: string;
  /** Renders the panel body. Called for every tab passed to Tabs. */
  renderPanel: () => ReactNode;
}

export interface TabsProps {
  /** Ordered, already-filtered list of AVAILABLE tabs. Tabs renders exactly these. */
  tabs: TabDescriptor[];
  /** Currently selected tab id. Must be one of tabs[].id. */
  selectedId: string;
  /** Called when the user selects a tab (click, Enter/Space, or arrow focus). */
  onSelect: (id: string) => void;
  /** Accessible name for the tablist, e.g. 'Position detail sections'. */
  ariaLabel: string;
}

export function Tabs({ tabs, selectedId, onSelect, ariaLabel }: TabsProps) {
  // Ref map keyed by tab id, used to imperatively move DOM focus to a tab button
  // when keyboard navigation changes the selected tab (automatic activation).
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  /** Select the tab at `index` (wrapped by the caller) and move focus to it. */
  function selectAndFocus(index: number) {
    const target = tabs[index];
    if (!target) {
      return;
    }
    onSelect(target.id);
    tabRefs.current[target.id]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) {
    switch (event.key) {
      case 'ArrowRight': {
        event.preventDefault();
        selectAndFocus((currentIndex + 1) % tabs.length);
        break;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        selectAndFocus((currentIndex - 1 + tabs.length) % tabs.length);
        break;
      }
      case 'Home': {
        event.preventDefault();
        selectAndFocus(0);
        break;
      }
      case 'End': {
        event.preventDefault();
        selectAndFocus(tabs.length - 1);
        break;
      }
      case 'Enter':
      case ' ': {
        // Explicit activation of the focused tab. Harmless under the automatic
        // activation model (arrow keys already selected on focus).
        event.preventDefault();
        onSelect(tabs[currentIndex].id);
        break;
      }
      default:
        break;
    }
  }

  return (
    <div>
      <div
        role="tablist"
        aria-label={ariaLabel}
        style={{ display: 'flex', gap: '0.25rem', borderBottom: '2px solid var(--color-border)' }}
      >
        {tabs.map((tab, index) => {
          const isSelected = tab.id === selectedId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-controls={`panel-${tab.id}`}
              aria-selected={isSelected}
              tabIndex={isSelected ? 0 : -1}
              ref={(el) => {
                tabRefs.current[tab.id] = el;
              }}
              onClick={() => onSelect(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              style={{
                padding: '0.75rem 1.25rem',
                border: 'none',
                borderBottom: isSelected ? '2px solid var(--color-brand)' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: isSelected ? 'var(--color-brand)' : 'var(--color-muted)',
                fontWeight: isSelected ? 600 : 400,
                cursor: 'pointer',
                minHeight: '44px',
                marginBottom: '-2px',
                fontSize: '0.95rem',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {tabs.map((tab) => {
        const isSelected = tab.id === selectedId;
        return (
          <div
            key={tab.id}
            role="tabpanel"
            id={`panel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            hidden={!isSelected}
            style={isSelected ? undefined : { display: 'none' }}
          >
            {tab.renderPanel()}
          </div>
        );
      })}
    </div>
  );
}
